import axios from "axios";
import { create } from "zustand";

interface ShapePaypalAccount {
  id: number;
  name: string;
  merchant_id: number;
  merchant_name: string | null;
  checkout_url: string | null;
}

interface ShapePaypalResolveParams {
  campaignId?: string | null;
  countryCode?: string | null;
  affiliateTag?: string | null;
  urlParameter?: string | null;
  postId?: number | string | null;
}

interface ShapePaypalState {
  resolved: boolean;
  available: boolean;
  likely: boolean;
  account: ShapePaypalAccount | null;
  rule_id: number | null;
  resolve: (params?: ShapePaypalResolveParams) => Promise<void>;
}

/**
 * Dynamic Shape-routed PayPal availability. Populated by a single resolve
 * call on checkout load (see FormWrapper.tsx). Any failure (network, Shape
 * down, site not opted in) is a safe no-op: available stays false and the
 * checkout keeps its existing/local PayPal behaviour untouched.
 *
 * `likely` seeds from window.shapePaypalLikelyAvailable - a PHP-side flag
 * set when any visitor matched a Shape routing rule from this checkout post
 * in roughly the last 5 minutes (see ShapePaypalAvailabilityCache on the
 * backend). It's keyed on the checkout post rather than the campaign id -
 * the campaign a visitor resolves against depends on affiliate-tag/cookie
 * routing and doesn't map 1:1 back to the post, so resolve() below sends
 * post_id alongside campaign_id purely so the cache write matches what
 * layout.php reads at render time. `likely` only ever widens what shows
 * optimistically before the real resolve() below completes; an order never
 * submits using it - only `available`/`account`/`rule_id` from a real
 * resolve response do that.
 */
export const useShapePaypalData = create<ShapePaypalState>((set) => ({
  resolved: false,
  available: false,
  likely: typeof window !== "undefined" && !!window.shapePaypalLikelyAvailable,
  account: null,
  rule_id: null,

  resolve: async (params = {}) => {
    const { campaignId, countryCode, affiliateTag, urlParameter, postId } =
      params;

    try {
      const res = await axios.get("/wp-json/checkout/v1/shape/resolve-paypal", {
        params: {
          campaign_id: campaignId || undefined,
          country: countryCode || undefined,
          affiliate_tag: affiliateTag || undefined,
          url_parameter: urlParameter || undefined,
          post_id: postId || undefined,
        },
      });

      set({
        resolved: true,
        available: !!res?.data?.available,
        account: res?.data?.account ?? null,
        rule_id: res?.data?.rule_id ?? null,
      });
    } catch (err) {
      set({ resolved: true, available: false, account: null });
    }
  },
}));
